// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

/**
 * Pure state machine for the F12 email + passcode onboarding flow.
 *
 * Separated from the React component so the transitions are unit-
 * testable without a DOM. The component is a thin renderer over
 * `reduce(state, event)` — all branching logic, including the
 * critical "server says match + no local seed → restore, NEVER
 * register" path, lives here.
 *
 * COB-646 (decision Q11): THIS REDUCER IS THE AUTHORITY. Every
 * onboarding transition is now produced here and nowhere else —
 * `pages/onboarding.tsx` holds the machine with `useReducer` and
 * writes it only through `dispatch`. Before COB-646 the component
 * performed 18 of the transitions with direct `setState` object
 * literals, so the arms pinned by `tests/auth-flow-pairs.test.ts`
 * were a promise about this file rather than about the shipping
 * flow. Do not reintroduce a direct state write in the component:
 * a transition that is not an arm below is not a transition.
 *
 * State transitions diagram:
 *
 *    initial
 *       │  SubmitCredentials
 *       ▼
 *    checking
 *       │
 *       ├── LookupNoAccount          → seedSetupBlocking
 *       │                               │  ConfirmBackup
 *       │                               │  Register
 *       │                               ▼
 *       │                            authenticated
 *       │
 *       ├── LookupMatchWithSeed      → unlocking
 *       │                               │  UnlockOk
 *       │                               │  ▼
 *       │                               │  authenticated
 *       │                               └─ UnlockFailedUnknownUser
 *       │                                    ▼
 *       │                                  error  (foreign local seed;
 *       │                                          terminal but for Reset)
 *       │
 *       ├── LookupMatchNoSeed        → restorePrompt
 *       │                               │  SubmitMnemonic
 *       │                               ▼
 *       │                            restoring
 *       │                               │  VerifyOk
 *       │                               │  ▼
 *       │                               │  authenticated
 *       │                               └─ VerifyFailedUnknownUser
 *       │                                    ▼
 *       │                                  restoreWrongSeed
 *       │
 *       ├── LookupMismatch           → mismatchOptions
 *       │                               │  ChooseRecover
 *       │                               ▼
 *       │                            recoverWithSeed  (collect seed + new passcode)
 *       │                               │  SubmitReset
 *       │                               ▼
 *       │                            authenticated
 *       │
 *       └── LookupWeb3AuthAccount    → error  (delegated-custody email;
 *                                              never enters seed setup)
 *
 * The direct regression guard for the original bug is baked in at
 * the `checking` state: `LookupMatchNoSeed` transitions to
 * `restorePrompt`, never to `seedSetupBlocking`.
 */

export type AuthFlowState =
  | { kind: 'initial' }
  | { kind: 'checking'; email: string }
  | { kind: 'seedSetupBlocking'; email: string }
  | { kind: 'unlocking'; email: string }
  | { kind: 'restorePrompt'; email: string }
  | { kind: 'restoring'; email: string }
  | { kind: 'restoreWrongSeed'; email: string }
  | { kind: 'mismatchOptions'; email: string }
  | { kind: 'recoverWithSeed'; email: string }
  | { kind: 'resetting'; email: string }
  | { kind: 'authenticated' }
  // COB-646 (doc 09 §7 Q4): `email` was `string | null`. No arm here and no
  // component write ever produced `null`, so the width was unreachable; it is
  // now `string` and the whole union carries an email except the two states
  // that predate one (`initial`, `authenticated`).
  | { kind: 'error'; email: string; message: string };

export type AuthFlowEvent =
  | { type: 'SubmitCredentials'; email: string }
  | { type: 'LookupNoAccount' }
  | { type: 'LookupMatchWithSeed' }
  | { type: 'LookupMatchNoSeed' }
  | { type: 'LookupMismatch' }
  // COB-646: the fourth `PasscodeCheckStatus` (`api.ts` `'web3auth'`). The
  // event alphabet could not express it, so the component handled it with a
  // direct `setState`. As an event, "an email registered for delegated
  // custody never enters the self-custody seed-setup branch" is a REDUCER
  // guarantee, pinned in the pair table beside `LookupMatchNoSeed`.
  | { type: 'LookupWeb3AuthAccount' }
  | { type: 'LookupError'; message: string }
  | { type: 'ConfirmSeedBackup' } // register_user call succeeded
  | { type: 'UnlockOk' } // /auth/verify returned 200 for the unlock path
  | { type: 'UnlockFailedUnknownUser' } // /auth/verify returned 401 unknown_user
  | { type: 'UnlockError'; message: string }
  | { type: 'SubmitMnemonic' } // user entered seed in restorePrompt
  | { type: 'RestoreOk' } // restore + SIWB auth both succeeded
  | { type: 'RestoreFailedUnknownUser' } // recovered pubkey doesn't match stored row
  | { type: 'RestoreError'; message: string }
  | { type: 'ChooseRetry' } // mismatchOptions → back to initial
  | { type: 'ChooseRecover' } // mismatchOptions → recoverWithSeed
  | { type: 'SubmitReset' } // user entered seed + new passcode in recoverWithSeed
  | { type: 'ResetOk' } // /auth/reset-passcode succeeded
  | { type: 'ResetError'; message: string }
  | { type: 'Reset' }; // hard reset to 'initial'

export const initialAuthFlowState: AuthFlowState = { kind: 'initial' };

/** COB-646: the message `checking + LookupWeb3AuthAccount` lands in. Owned by
 *  the reducer, not the component, so the copy cannot vary by call site.
 *  Byte-identical to the string `onboarding.tsx` wrote directly before this
 *  ticket — the routing moved, the words did not. */
export const WEB3AUTH_ACCOUNT_MESSAGE =
  'This email is registered with web wallet sign-in. Go back and choose “web3auth (social login)”.';

/** COB-646 (doc 09 §7 Q3): the message `unlocking + UnlockFailedUnknownUser`
 *  lands in.
 *
 *  That arm is reached when the server accepted the (email, passcode) pair but
 *  the seed already on this device derives an identity pubkey the server does
 *  not hold for that email — i.e. the local seed belongs to someone else's
 *  account. It used to route to `mismatchOptions`, whose copy ("that passcode
 *  isn't right") is false on this path and whose only forward option,
 *  `ChooseRecover → recoverWithSeed → SubmitReset`, cannot succeed:
 *  `resetPasscodeWithSeed` (`session-context.tsx`) skips `restoreSeed` when
 *  `hasSeed()` is already true — which it is here — so the typed mnemonic is
 *  discarded, the stale foreign pubkey is what gets challenged, and the server
 *  401s. The arm offered a route whose only outcome was failure.
 *
 *  It now lands in `error`, which is terminal but for `Reset`. That is the
 *  fail-closed choice: no state that creates or overwrites a seed is reachable
 *  from here at all, and the only way back to seed generation is a full
 *  `Reset` → fresh lookup, which reaches `seedSetupBlocking` only on a server
 *  `no_account` — the same condition `clearOrphanSeedForNewAccount`
 *  re-confirms with the server immediately before it clears anything. */
export const FOREIGN_LOCAL_SEED_MESSAGE =
  'Your passcode was accepted, but the seed phrase stored in this browser ' +
  'belongs to a different account, so it cannot unlock this one. Sign in with ' +
  'the email that seed phrase belongs to, or use a browser with no Cobault ' +
  'seed stored and restore this account there.';

export function reduceAuthFlow(
  state: AuthFlowState,
  event: AuthFlowEvent,
): AuthFlowState {
  // Reset is always allowed — lets the user back out of any error or
  // terminal branch to the initial email+passcode form.
  if (event.type === 'Reset') return initialAuthFlowState;

  switch (state.kind) {
    case 'initial':
      if (event.type === 'SubmitCredentials') {
        return { kind: 'checking', email: event.email };
      }
      return state;

    case 'checking':
      switch (event.type) {
        case 'LookupNoAccount':
          return { kind: 'seedSetupBlocking', email: state.email };
        case 'LookupMatchWithSeed':
          return { kind: 'unlocking', email: state.email };
        // CRITICAL: the regression guard. Server says the (email,
        // passcode) pair exists but the device has no seed →
        // restorePrompt, never seedSetupBlocking. This is the
        // server-side half of the fix the original bug needs.
        case 'LookupMatchNoSeed':
          return { kind: 'restorePrompt', email: state.email };
        case 'LookupMismatch':
          return { kind: 'mismatchOptions', email: state.email };
        // COB-646: the delegated-custody (Web3Auth) email. There is no
        // passcode to check and no self-custody seed to set up, so this lands
        // in `error` with steering copy — and, structurally, in the one arm of
        // `checking` from which `seedSetupBlocking` is unreachable without a
        // full Reset and a fresh server lookup.
        case 'LookupWeb3AuthAccount':
          return {
            kind: 'error',
            email: state.email,
            message: WEB3AUTH_ACCOUNT_MESSAGE,
          };
        case 'LookupError':
          return { kind: 'error', email: state.email, message: event.message };
        default:
          return state;
      }

    case 'seedSetupBlocking':
      if (event.type === 'ConfirmSeedBackup') {
        return { kind: 'authenticated' };
      }
      return state;

    case 'unlocking':
      switch (event.type) {
        case 'UnlockOk':
          return { kind: 'authenticated' };
        case 'UnlockFailedUnknownUser':
          // The device has a seed, but the derived pubkey is not registered
          // for this email — i.e. the local seed belongs to someone else's
          // account. COB-646 (doc 09 §7 Q3): this used to route to
          // `mismatchOptions`, whose only forward option can never succeed on
          // this path. It now terminates in `error` — see
          // FOREIGN_LOCAL_SEED_MESSAGE for the full rationale and for why
          // that is the fail-closed answer.
          return {
            kind: 'error',
            email: state.email,
            message: FOREIGN_LOCAL_SEED_MESSAGE,
          };
        case 'UnlockError':
          return { kind: 'error', email: state.email, message: event.message };
        default:
          return state;
      }

    case 'restorePrompt':
      if (event.type === 'SubmitMnemonic') {
        return { kind: 'restoring', email: state.email };
      }
      return state;

    case 'restoring':
      switch (event.type) {
        case 'RestoreOk':
          return { kind: 'authenticated' };
        case 'RestoreFailedUnknownUser':
          return { kind: 'restoreWrongSeed', email: state.email };
        case 'RestoreError':
          return { kind: 'error', email: state.email, message: event.message };
        default:
          return state;
      }

    case 'restoreWrongSeed':
      // User can retry with a different seed or back out to initial (via
      // Reset, handled above). COB-646: this arm is now the live one — the
      // component's "Try again" re-renders the seed grid WITHIN
      // `restoreWrongSeed` (a local render step, not a transition) and the
      // retry submits `SubmitMnemonic` from here. Before COB-646 the button
      // wrote `{ kind: 'restorePrompt' }` directly, a transition no arm in
      // this reducer expressed, and this arm was unreachable in production.
      if (event.type === 'SubmitMnemonic') {
        return { kind: 'restoring', email: state.email };
      }
      return state;

    case 'mismatchOptions':
      switch (event.type) {
        case 'ChooseRetry':
          return initialAuthFlowState;
        case 'ChooseRecover':
          return { kind: 'recoverWithSeed', email: state.email };
        default:
          return state;
      }

    case 'recoverWithSeed':
      if (event.type === 'SubmitReset') {
        return { kind: 'resetting', email: state.email };
      }
      return state;

    case 'resetting':
      switch (event.type) {
        case 'ResetOk':
          return { kind: 'authenticated' };
        case 'ResetError':
          return { kind: 'error', email: state.email, message: event.message };
        default:
          return state;
      }

    case 'authenticated':
    case 'error':
      return state;
  }
}

/** Client-side passcode minimum length. Mirrors the F12 plan
 *  decision; enforced before calling deriveVerifier. */
export const PASSCODE_MIN_LENGTH = 12;

export function validatePasscode(passcode: string): string | null {
  if (passcode.length < PASSCODE_MIN_LENGTH) {
    return `Passcode must be at least ${PASSCODE_MIN_LENGTH} characters`;
  }
  return null;
}

/** COB-646 (doc 09 §7 Q6): DELIBERATELY NOT re-asserted inside
 *  `reduceAuthFlow`. `initial + SubmitCredentials` still accepts any string.
 *  Two reasons, both structural rather than stylistic: the component has to
 *  call `validateEmail` itself regardless (it cannot read the post-dispatch
 *  state synchronously, so it needs its own answer to decide whether to run
 *  the lookup at all), which would leave the rule stated twice with two places
 *  to drift; and `validatePasscode` can never move here, because the passcode
 *  is deliberately kept out of the state machine (it lives in a ref so React
 *  never re-renders with plaintext), so a reducer-side email guard would close
 *  half the boundary while reading as if it closed all of it. The single guard
 *  sits at the one call site all three mount points share,
 *  `onboarding.tsx handleInitialSubmit`. */
export function validateEmail(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return 'Email is required';
  if (trimmed.length > 320) return 'Email is too long';
  const [local, domain] = trimmed.split('@');
  if (!local || !domain) return 'Email must contain "@"';
  if (!domain.includes('.')) return 'Email domain must contain "."';
  return null;
}
